import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { organizationsService, plansService, categoriesService } from '../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Toggle Switch Component - Standard Toggle
const ToggleSwitch = ({ checked, onChange, disabled = false }) => {
  return (
    <div 
      className="toggle-switch-container"
      style={{ 
        position: 'relative',
        display: 'inline-block',
        width: '50px',
        height: '26px',
        cursor: disabled ? 'not-allowed' : 'pointer'
      }}
      onClick={disabled ? undefined : onChange}
    >
      <style>{`
        .toggle-switch-container input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-switch-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 26px;
        }
        .toggle-switch-slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .toggle-switch-container input:checked + .toggle-switch-slider {
          background-color: #28a745;
        }
        .toggle-switch-container input:checked + .toggle-switch-slider:before {
          transform: translateX(24px);
        }
        .toggle-switch-container input:disabled + .toggle-switch-slider {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span className="toggle-switch-slider"></span>
    </div>
  );
};

const OrganizationsTable = ({ status = 'all', title = 'Organizasyonlar' }) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showEndDateModal, setShowEndDateModal] = useState(false);
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [limits, setLimits] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [editForm, setEditForm] = useState({ 
    name: '', 
    title: '', 
    city: '', 
    district: '', 
    address: '', 
    phone: '',
    category_ids: []
  });
  const [allCategories, setAllCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    planId: '',
    endsAt: ''
  });
  const [endDateForm, setEndDateForm] = useState({
    endsAt: ''
  });
  const [limitsForm, setLimitsForm] = useState({
    sms_limit: '',
    email_limit: '',
    whatsapp_limit: '',
    file_storage_limit_gb: ''
  });

  useEffect(() => {
    loadOrganizations();
    loadPlans();
  }, [status, pagination.page, pagination.limit, filters]);

  const loadPlans = async () => {
    try {
      const response = await plansService.getAllPlans();
      if (response.success) {
        setPlans(response.data || []);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      const response = status === 'all' 
        ? await organizationsService.getAllOrganizations(params)
        : await organizationsService.getOrganizationsByStatus(status, params);

      if (response.success) {
        setOrganizations(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Organizasyonlar yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
      toast.error('Organizasyonlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleToggleStatus = async (org) => {
    const response = await organizationsService.toggleOrganizationStatus(org.id);
    if (response.success) {
      toast.success(response.message || 'Durum güncellendi');
      loadOrganizations();
    } else {
      toast.error(response.error || 'Durum güncellenemedi');
    }
  };

  const handleToggleListedStatus = async (org) => {
    const response = await organizationsService.toggleOrganizationListedStatus(org.id);
    if (response.success) {
      toast.success(response.message || 'Vitrin durumu güncellendi');
      loadOrganizations();
    } else {
      toast.error(response.error || 'Vitrin durumu güncellenemedi');
    }
  };

  const handleToggleSubscriptionStatus = async (org) => {
    const response = await organizationsService.toggleSubscriptionStatus(org.id);
    if (response.success) {
      toast.success(response.message || 'Abonelik durumu güncellendi');
      loadOrganizations();
    } else {
      toast.error(response.error || 'Abonelik durumu güncellenemedi');
    }
  };

  const handleEdit = async (org) => {
    setSelectedOrg(org);
    setEditForm({
      name: org.name || '',
      title: org.title || '',
      city: org.city || '',
      district: org.district || '',
      address: org.address || '',
      phone: org.phone || '',
      category_ids: []
    });
    setShowEditModal(true);

    try {
      if (!categoriesLoaded) {
        const catsRes = await categoriesService.getAllFlat();
        if (catsRes.success) {
          setAllCategories(catsRes.data || []);
          setCategoriesLoaded(true);
        }
      }
      const orgCatsRes = await organizationsService.getOrganizationCategories(org.id);
      if (orgCatsRes.success) {
        setEditForm(prev => ({
          ...prev,
          category_ids: (orgCatsRes.data || []).map(c => c.id)
        }));
      }
    } catch (err) {
      console.error('❌ Edit modal kategori yükleme hatası:', err);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedOrg) return;

    const payload = { ...editForm };
    const response = await organizationsService.updateOrganization(selectedOrg.id, payload);
    if (response.success) {
      toast.success('Organizasyon güncellendi');
      setShowEditModal(false);
      loadOrganizations();
    } else {
      toast.error(response.error || 'Organizasyon güncellenemedi');
    }
  };

  const toggleEditCategory = (id) => {
    setEditForm(prev => {
      const has = prev.category_ids.includes(id);
      return {
        ...prev,
        category_ids: has
          ? prev.category_ids.filter(x => x !== id)
          : [...prev.category_ids, id]
      };
    });
  };

  const handleOpenSubscriptionModal = async (org) => {
    setSelectedOrg(org);
    setLoadingData(true);
    try {
      // Plans'ı her zaman yükle (güncel olsun)
      const plansResponse = await plansService.getAllPlans();
      console.log('📦 Plans response:', plansResponse);
      if (plansResponse.success) {
        setPlans(plansResponse.data || []);
        console.log('📦 Plans loaded:', plansResponse.data?.length || 0);
      } else {
        console.error('❌ Plans not loaded:', plansResponse);
        toast.error('Planlar yüklenemedi');
      }
      
      const subResponse = await organizationsService.getOrganizationSubscription(org.id);
      if (subResponse.success) {
        setSubscription(subResponse.data);
        setSubscriptionForm({
          planId: subResponse.data?.plan_id || '',
          endsAt: subResponse.data?.ends_at ? subResponse.data.ends_at.split('T')[0] : ''
        });
      }
      setShowSubscriptionModal(true);
    } catch (error) {
      console.error('❌ Error loading subscription:', error);
      toast.error('Abonelik bilgileri yüklenemedi');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSaveSubscription = async () => {
    if (!selectedOrg || !subscriptionForm.planId) {
      toast.error('Lütfen bir plan seçin');
      return;
    }

    const response = await organizationsService.updateOrganizationSubscription(
      selectedOrg.id,
      subscriptionForm
    );
    if (response.success) {
      toast.success('Abonelik paketi güncellendi');
      setShowSubscriptionModal(false);
      loadOrganizations();
    } else {
      toast.error(response.error || 'Abonelik paketi güncellenemedi');
    }
  };

  const handleOpenEndDateModal = async (org) => {
    setSelectedOrg(org);
    setLoadingData(true);
    try {
      const subResponse = await organizationsService.getOrganizationSubscription(org.id);
      if (subResponse.success) {
        setSubscription(subResponse.data);
        if (subResponse.data) {
          setEndDateForm({
            endsAt: subResponse.data.ends_at ? subResponse.data.ends_at.split('T')[0] : ''
          });
        } else {
          // Eğer abonelik yoksa, organizasyondan gelen tarihi kullan
          setEndDateForm({
            endsAt: org.subscription_end_date ? org.subscription_end_date.split('T')[0] : org.demo_end_date ? org.demo_end_date.split('T')[0] : ''
          });
        }
      }
      setShowEndDateModal(true);
    } catch (error) {
      toast.error('Abonelik bilgileri yüklenemedi');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSaveEndDate = async () => {
    if (!selectedOrg || !endDateForm.endsAt) {
      toast.error('Lütfen bitiş tarihi girin');
      return;
    }

    const response = await organizationsService.updateSubscriptionEndDate(
      selectedOrg.id,
      endDateForm.endsAt
    );
    if (response.success) {
      toast.success('Bitiş tarihi güncellendi');
      setShowEndDateModal(false);
      loadOrganizations();
    } else {
      toast.error(response.error || 'Bitiş tarihi güncellenemedi');
    }
  };

  const handleOpenLimitsModal = async (org) => {
    setSelectedOrg(org);
    setLoadingData(true);
    try {
      const limitsResponse = await organizationsService.getOrganizationLimits(org.id);
      console.log('📊 Limits response:', limitsResponse);
      if (limitsResponse.success) {
        setLimits(limitsResponse.data || {});
        const notificationLimits = limitsResponse.data?.notification || null;
        const fileLimits = limitsResponse.data?.file || null;
        
        // Eğer limitler yoksa, boş string yerine 0 veya varsayılan değerler kullan
        setLimitsForm({
          sms_limit: notificationLimits?.sms_limit !== undefined && notificationLimits?.sms_limit !== null ? notificationLimits.sms_limit : '',
          email_limit: notificationLimits?.email_limit !== undefined && notificationLimits?.email_limit !== null ? notificationLimits.email_limit : '',
          whatsapp_limit: notificationLimits?.whatsapp_limit !== undefined && notificationLimits?.whatsapp_limit !== null ? notificationLimits.whatsapp_limit : '',
          file_storage_limit_gb: fileLimits?.total_storage_limit_gb !== undefined && fileLimits?.total_storage_limit_gb !== null ? fileLimits.total_storage_limit_gb : ''
        });
        console.log('📊 Limits form set:', {
          notificationLimits,
          fileLimits,
          form: {
            sms: notificationLimits?.sms_limit,
            email: notificationLimits?.email_limit,
            whatsapp: notificationLimits?.whatsapp_limit,
            storage: fileLimits?.total_storage_limit_gb
          }
        });
      } else {
        console.error('❌ Limits response not successful:', limitsResponse);
        // Limitler yoksa bile modal'ı aç, boş form ile
        setLimits({ notification: null, file: null });
        setLimitsForm({
          sms_limit: '',
          email_limit: '',
          whatsapp_limit: '',
          file_storage_limit_gb: ''
        });
      }
      setShowLimitsModal(true);
    } catch (error) {
      console.error('❌ Error loading limits:', error);
      // Hata olsa bile modal'ı aç
      setLimits({ notification: null, file: null });
      setLimitsForm({
        sms_limit: '',
        email_limit: '',
        whatsapp_limit: '',
        file_storage_limit_gb: ''
      });
      setShowLimitsModal(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleSaveLimits = async () => {
    if (!selectedOrg) return;

    const response = await organizationsService.updateOrganizationLimits(
      selectedOrg.id,
      limitsForm
    );
    if (response.success) {
      toast.success('Limitler güncellendi');
      setShowLimitsModal(false);
      loadOrganizations();
    } else {
      toast.error(response.error || 'Limitler güncellenemedi');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatSubscriptionEndDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <>
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">Göster</span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={pagination.limit}
              onChange={handlePageSizeChange}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <form className="navbar-search">
              <input
                type="text"
                className="bg-base h-40-px w-auto"
                name="search"
                placeholder="Ara (Ad, Ünvan, Şehir, İlçe)..."
                value={filters.search}
                onChange={handleSearch}
              />
              <Icon icon="ion:search-outline" className="icon" />
            </form>
          </div>
        </div>
        <div className="card-body p-24">
          {loading ? (
            <div className="text-center py-5">
              <p className="text-secondary-light">Yükleniyor...</p>
            </div>
          ) : organizations.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-secondary-light">Organizasyon bulunamadı</p>
            </div>
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">S.L</th>
                      <th scope="col">Kayıt Tarihi</th>
                      <th scope="col">Organizasyon Adı</th>
                      <th scope="col">Ünvan</th>
                      <th scope="col">Şehir</th>
                      <th scope="col">İlçe</th>
                      <th scope="col">Owner</th>
                      <th scope="col">Abonelik Paketi</th>
                      {(status === 'subscription' || status === 'demo') && (
                        <>
                          {status === 'subscription' && (
                            <th scope="col">Abonelik Bitiş</th>
                          )}
                          {status === 'demo' && (
                            <th scope="col">Demo Bitiş</th>
                          )}
                        </>
                      )}
                      <th scope="col" className="text-center">Durum</th>
                      <th scope="col" className="text-center">Vitrin</th>
                      <th scope="col" className="text-center">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {organizations.map((org, index) => (
                      <tr key={org.id}>
                        <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                        <td>{formatDate(org.created_at)}</td>
                        <td>
                          <span className="text-md mb-0 fw-medium">
                            {org.name || '-'}
                          </span>
                        </td>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {org.title || '-'}
                          </span>
                        </td>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {org.city || '-'}
                          </span>
                        </td>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {org.district || '-'}
                          </span>
                        </td>
                        <td>
                          <div>
                            <span className="text-md mb-0 fw-normal">
                              {org.owner_name || '-'}
                            </span>
                            {org.owner_email && (
                              <div className="text-xs text-secondary-light">
                                {org.owner_email}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div>
                            {org.demo_end_date && (!org.subscription_end_date || new Date(org.demo_end_date) > new Date(org.subscription_end_date)) ? (
                              <>
                                <span className="badge bg-info">
                                  Demo
                                </span>
                                {status === 'active' && org.demo_end_date && (
                                  <div className="text-xs text-secondary-light mt-1">
                                    {formatSubscriptionEndDate(org.demo_end_date)}
                                  </div>
                                )}
                              </>
                            ) : org.subscription_package ? (
                              <>
                                <span className="badge bg-primary">
                                  {org.subscription_package}
                                </span>
                                {status === 'active' && org.subscription_end_date && (
                                  <div className="text-xs text-secondary-light mt-1">
                                    {formatSubscriptionEndDate(org.subscription_end_date)}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="badge bg-secondary">Abonelik Yok</span>
                            )}
                          </div>
                        </td>
                        {(status === 'subscription' || status === 'demo') && (
                          <td>
                            {status === 'subscription' && org.subscription_end_date ? (
                              <div>
                                <div className="text-sm fw-medium">
                                  {formatSubscriptionEndDate(org.subscription_end_date)}
                                </div>
                                {org.subscription_days_remaining !== null && (
                                  <span className={`text-xs ${org.subscription_days_remaining < 30 ? 'text-danger' : org.subscription_days_remaining < 60 ? 'text-warning' : 'text-success'}`}>
                                    {org.subscription_days_remaining} gün kaldı
                                  </span>
                                )}
                              </div>
                            ) : status === 'demo' && org.demo_end_date ? (
                              <div>
                                <div className="text-sm fw-medium">
                                  {formatSubscriptionEndDate(org.demo_end_date)}
                                </div>
                                {org.demo_days_remaining !== null && (
                                  <span className={`text-xs ${org.demo_days_remaining < 30 ? 'text-danger' : org.demo_days_remaining < 60 ? 'text-warning' : 'text-success'}`}>
                                    {org.demo_days_remaining} gün kaldı
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-secondary-light">-</span>
                            )}
                          </td>
                        )}
                        <td className="text-center">
                          <ToggleSwitch
                            checked={org.active}
                            onChange={() => handleToggleStatus(org)}
                          />
                        </td>
                        <td className="text-center">
                          <ToggleSwitch
                            checked={Number(org.randu_listed) === 1 || org.vitrin_listed === true}
                            onChange={() => handleToggleListedStatus(org)}
                          />
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-10 justify-content-center flex-wrap">
                            <button
                              type="button"
                              className="bg-primary-focus text-primary-600 bg-hover-primary-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleEdit(org)}
                              title="Düzenle"
                            >
                              <Icon icon="lucide:edit" className="icon text-lg" />
                            </button>
                            <button
                              type="button"
                              className="bg-primary-focus text-primary-600 bg-hover-primary-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleOpenSubscriptionModal(org)}
                              title="Paket Değiştir"
                            >
                              <Icon icon="lucide:package" className="icon text-lg" />
                            </button>
                            <button
                              type="button"
                              className="bg-primary-focus text-primary-600 bg-hover-primary-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleOpenEndDateModal(org)}
                              title="Bitiş Tarihi Güncelle"
                            >
                              <Icon icon="lucide:calendar" className="icon text-lg" />
                            </button>
                            <button
                              type="button"
                              className="bg-primary-focus text-primary-600 bg-hover-primary-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleOpenLimitsModal(org)}
                              title="Limitleri Güncelle"
                            >
                              <Icon icon="lucide:settings" className="icon text-lg" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="d-flex align-items-center justify-content-between mt-24">
                  <div className="text-secondary-light">
                    Toplam {pagination.total} kayıt, {pagination.page}/{pagination.totalPages} sayfa
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={pagination.page === 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      Önceki
                    </button>
                    {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                      const pageNum = pagination.page <= 3 
                        ? i + 1 
                        : pagination.page >= pagination.totalPages - 2
                        ? pagination.totalPages - 4 + i
                        : pagination.page - 2 + i;
                      if (pageNum < 1 || pageNum > pagination.totalPages) return null;
                      return (
                        <button
                          key={pageNum}
                          className={`btn btn-sm ${pagination.page === pageNum ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Sonraki
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedOrg && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Organizasyon Düzenle</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEditModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Organizasyon Adı</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Ünvan</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.title}
                      onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Şehir</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.city}
                      onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">İlçe</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.district}
                      onChange={(e) => setEditForm(prev => ({ ...prev, district: e.target.value }))}
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <label className="form-label">Adres</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={editForm.address}
                      onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Telefon</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.phone}
                      onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <label className="form-label">
                      Kategoriler
                      <span className="text-secondary-light ms-2" style={{ fontSize: 12 }}>
                        (Seçim: {editForm.category_ids.length})
                      </span>
                    </label>
                    {!categoriesLoaded && allCategories.length === 0 ? (
                      <div className="text-secondary-light py-2" style={{ fontSize: 13 }}>
                        Kategoriler yükleniyor...
                      </div>
                    ) : (
                      <div
                        className="border rounded p-2"
                        style={{ maxHeight: 200, overflowY: 'auto' }}
                      >
                        <div className="d-flex flex-wrap gap-2">
                          {allCategories.map(cat => {
                            const selected = editForm.category_ids.includes(cat.id);
                            return (
                              <button
                                key={cat.id}
                                type="button"
                                className={`btn btn-sm ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => toggleEditCategory(cat.id)}
                              >
                                {cat.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <small className="text-secondary-light">
                      İstediğiniz kategorileri tıklayarak seçin. İlk seçilen kategori "ana kategori" olarak kaydedilir.
                    </small>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveEdit}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && selectedOrg && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Abonelik Paketi Değiştir</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSubscriptionModal(false)}
                />
              </div>
              <div className="modal-body">
                {loadingData ? (
                  <div className="text-center py-4">
                    <p className="text-secondary-light">Yükleniyor...</p>
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-12 mb-3">
                      <label className="form-label">Mevcut Paket</label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          selectedOrg?.demo_end_date && (!selectedOrg?.subscription_end_date || new Date(selectedOrg.demo_end_date) > new Date(selectedOrg.subscription_end_date))
                            ? 'Demo'
                            : subscription?.plan_name || selectedOrg?.subscription_package || 'Abonelik Yok'
                        }
                        disabled
                      />
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">Yeni Paket <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        value={subscriptionForm.planId}
                        onChange={(e) => setSubscriptionForm(prev => ({ ...prev, planId: e.target.value }))}
                      >
                        <option value="">Paket Seçin</option>
                        {plans.length > 0 ? (
                          plans.map(plan => (
                            <option key={plan.id} value={plan.id}>
                              {plan.name} ({plan.plan_interval === 'trial' ? 'Demo' : plan.plan_interval === 'monthly' ? 'Aylık' : 'Yıllık'}) - {plan.price} {plan.currency}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>Planlar yükleniyor...</option>
                        )}
                      </select>
                      {plans.length === 0 && !loadingData && (
                        <small className="text-danger d-block mt-1">Planlar yüklenemedi. Lütfen sayfayı yenileyin.</small>
                      )}
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">Bitiş Tarihi (Opsiyonel)</label>
                      <input
                        type="date"
                        className="form-control"
                        value={subscriptionForm.endsAt}
                        onChange={(e) => setSubscriptionForm(prev => ({ ...prev, endsAt: e.target.value }))}
                      />
                      <small className="text-secondary-light">Boş bırakılırsa paket tipine göre otomatik hesaplanır</small>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowSubscriptionModal(false)}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveSubscription}
                  disabled={loadingData || !subscriptionForm.planId}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* End Date Modal */}
      {showEndDateModal && selectedOrg && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Bitiş Tarihi Güncelle</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEndDateModal(false)}
                />
              </div>
              <div className="modal-body">
                {loadingData ? (
                  <div className="text-center py-4">
                    <p className="text-secondary-light">Yükleniyor...</p>
                  </div>
                ) : (
                  <div>
                    {(subscription?.ends_at || selectedOrg?.subscription_end_date || selectedOrg?.demo_end_date) && (
                      <div className="mb-3">
                        <label className="form-label text-secondary-light">Mevcut Bitiş Tarihi</label>
                        <input
                          type="text"
                          className="form-control"
                          value={
                            subscription?.ends_at 
                              ? new Date(subscription.ends_at).toLocaleDateString('tr-TR')
                              : selectedOrg?.subscription_end_date
                              ? new Date(selectedOrg.subscription_end_date).toLocaleDateString('tr-TR')
                              : selectedOrg?.demo_end_date
                              ? new Date(selectedOrg.demo_end_date).toLocaleDateString('tr-TR')
                              : '-'
                          }
                          disabled
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                    )}
                    <div className="mb-3">
                      <label className="form-label">Yeni Bitiş Tarihi <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className="form-control"
                        value={endDateForm.endsAt}
                        onChange={(e) => setEndDateForm(prev => ({ ...prev, endsAt: e.target.value }))}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEndDateModal(false)}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveEndDate}
                  disabled={loadingData || !endDateForm.endsAt}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Limits Modal */}
      {showLimitsModal && selectedOrg && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Limitleri Güncelle</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowLimitsModal(false)}
                />
              </div>
              <div className="modal-body">
                {loadingData ? (
                  <div className="text-center py-4">
                    <p className="text-secondary-light">Yükleniyor...</p>
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-12 mb-3">
                      <h6 className="text-primary mb-3">Bildirim Limitleri</h6>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        SMS Limiti
                        {limits?.notification && limits.notification.sms_limit !== undefined && limits.notification.sms_limit !== null && (
                          <small className="text-secondary-light ms-2">(Mevcut: {limits.notification.sms_limit === -1 ? 'Sınırsız' : limits.notification.sms_limit})</small>
                        )}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={limitsForm.sms_limit}
                        onChange={(e) => setLimitsForm(prev => ({ ...prev, sms_limit: e.target.value }))}
                        placeholder="Sınırsız için -1"
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Email Limiti
                        {limits?.notification && limits.notification.email_limit !== undefined && limits.notification.email_limit !== null && (
                          <small className="text-secondary-light ms-2">(Mevcut: {limits.notification.email_limit === -1 ? 'Sınırsız' : limits.notification.email_limit})</small>
                        )}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={limitsForm.email_limit}
                        onChange={(e) => setLimitsForm(prev => ({ ...prev, email_limit: e.target.value }))}
                        placeholder="Sınırsız için -1"
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        WhatsApp Limiti
                        {limits?.notification && limits.notification.whatsapp_limit !== undefined && limits.notification.whatsapp_limit !== null && (
                          <small className="text-secondary-light ms-2">(Mevcut: {limits.notification.whatsapp_limit === -1 ? 'Sınırsız' : limits.notification.whatsapp_limit})</small>
                        )}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={limitsForm.whatsapp_limit}
                        onChange={(e) => setLimitsForm(prev => ({ ...prev, whatsapp_limit: e.target.value }))}
                        placeholder="Sınırsız için -1"
                      />
                    </div>
                    <div className="col-12 mb-3">
                      <h6 className="text-primary mb-3">Dosya Depolama Limitleri</h6>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Dosya Depolama Limiti (GB)
                        {limits?.file && limits.file.total_storage_limit_gb !== undefined && limits.file.total_storage_limit_gb !== null && (
                          <small className="text-secondary-light ms-2">(Mevcut: {limits.file.total_storage_limit_gb} GB)</small>
                        )}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={limitsForm.file_storage_limit_gb}
                        onChange={(e) => setLimitsForm(prev => ({ ...prev, file_storage_limit_gb: e.target.value }))}
                        placeholder="GB cinsinden"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowLimitsModal(false)}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveLimits}
                  disabled={loadingData}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrganizationsTable;

